// frontend/src/components/dedup/DuplicateRuleConfig.tsx
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControlLabel,
  Checkbox,
  Slider,
  Alert,
  Paper,
  Chip
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Home as HomeIcon,
  HomeWork as HomeWorkIcon
} from '@mui/icons-material';

interface DedupRule {
  match_fields: string[];
  match_threshold: number;
}

interface Props {
  campaignId: number;
  rule: DedupRule;
  onRuleChange: (rule: DedupRule) => void;
}

const matchFieldOptions = [
  {
    value: 'telefon',
    label: 'Telefonnummer',
    icon: <PhoneIcon />,
    description: 'Exakter Abgleich der Telefonnummer (normalisiert)',
    type: 'exact'
  },
  {
    value: 'email',
    label: 'E-Mail-Adresse',
    icon: <EmailIcon />,
    description: 'Exakter Abgleich der E-Mail-Adresse',
    type: 'exact'
  },
  {
    value: 'person_match',
    label: 'Personen-Match',
    icon: <PersonIcon />,
    description: 'Ähnlichkeit: Vorname + Nachname + Geburtsdatum',
    type: 'fuzzy'
  },
  {
    value: 'haushalt_match',
    label: 'Haushalts-Match',
    icon: <HomeIcon />,
    description: 'Ähnlichkeit: Nachname + Straße + Hausnummer + PLZ',
    type: 'fuzzy'
  },
  {
    value: 'haus_match',
    label: 'Haus-Match',
    icon: <HomeWorkIcon />,
    description: 'Ähnlichkeit: Straße + Hausnummer + PLZ',
    type: 'fuzzy'
  }
];

export default function DuplicateRuleConfig({ campaignId, rule, onRuleChange }: Props) {
  const handleFieldToggle = (field: string) => {
    const newFields = rule.match_fields.includes(field)
      ? rule.match_fields.filter(f => f !== field)
      : [...rule.match_fields, field];
    
    onRuleChange({ ...rule, match_fields: newFields });
  };

  const handleThresholdChange = (value: number) => {
    onRuleChange({ ...rule, match_threshold: value });
  };

  const hasFuzzyFields = rule.match_fields.some(field => 
    ['person_match', 'haushalt_match', 'haus_match'].includes(field)
  );

  const getMatchTypeColor = (type: string) => {
    return type === 'exact' ? 'success' : 'warning';
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Duplikats-Erkennungs-Regeln
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Wichtig:</strong> Ein Datensatz gilt als Duplikat, wenn mindestens eines 
        der ausgewählten Kriterien zutrifft. Bei Fuzzy-Matching wird der konfigurierte 
        Schwellenwert verwendet.
      </Alert>

      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        gap: 3 
      }}>
        {matchFieldOptions.map((option) => (
          <Paper
            key={option.value}
            elevation={rule.match_fields.includes(option.value) ? 3 : 1}
            sx={{
              p: 2,
              cursor: 'pointer',
              border: rule.match_fields.includes(option.value) 
                ? '2px solid' 
                : '2px solid transparent',
              borderColor: rule.match_fields.includes(option.value) 
                ? 'primary.main' 
                : 'transparent',
              transition: 'all 0.3s'
            }}
            onClick={() => handleFieldToggle(option.value)}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
              <Box sx={{ color: 'primary.main', mt: 0.5 }}>
                {option.icon}
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rule.match_fields.includes(option.value)}
                        onChange={() => handleFieldToggle(option.value)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={
                      <Typography variant="subtitle1" fontWeight="bold">
                        {option.label}
                      </Typography>
                    }
                  />
                  <Chip 
                    label={option.type === 'exact' ? 'Exakt' : 'Fuzzy'}
                    color={getMatchTypeColor(option.type)}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {option.description}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>

      {hasFuzzyFields && (
        <Card sx={{ mt: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Fuzzy-Match Schwellenwert
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Mindest-Ähnlichkeit in Prozent für Fuzzy-Matching (Person, Haushalt, Haus)
            </Typography>

            <Box sx={{ px: 2 }}>
              <Slider
                value={rule.match_threshold}
                onChange={(_, value) => handleThresholdChange(value as number)}
                min={60}
                max={100}
                step={5}
                marks={[
                  { value: 60, label: '60% (tolerant)' },
                  { value: 70, label: '70%' },
                  { value: 80, label: '80%' },
                  { value: 90, label: '90%' },
                  { value: 100, label: '100% (streng)' }
                ]}
                valueLabelDisplay="on"
                valueLabelFormat={(value) => `${value}%`}
              />
            </Box>

            <Alert severity="warning" sx={{ mt: 3 }}>
              <strong>Empfehlung:</strong> Beginnen Sie mit 80-90% und passen Sie den Wert 
              nach ersten Tests an. Zu niedrige Werte führen zu False Positives, zu hohe 
              Werte übersehen echte Duplikate.
            </Alert>
          </CardContent>
        </Card>
      )}

      <Card sx={{ mt: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Zusammenfassung
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Ausgewählte Kriterien:</strong>
            </Typography>
            {rule.match_fields.length === 0 ? (
              <Alert severity="warning">Keine Kriterien ausgewählt</Alert>
            ) : (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                {rule.match_fields.map(field => {
                  const option = matchFieldOptions.find(o => o.value === field);
                  return (
                    <Chip 
                      key={field}
                      label={option?.label}
                      color="primary"
                      icon={option?.icon}
                    />
                  );
                })}
              </Box>
            )}
            {hasFuzzyFields && (
              <Typography variant="body2" sx={{ mt: 2 }}>
                <strong>Fuzzy-Match Schwellenwert:</strong> {rule.match_threshold}%
              </Typography>
            )}
          </Box>
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Tipp:</strong> Sie können nach der Deduplizierung noch einzelne 
          Duplikate manuell überprüfen und korrigieren.
        </Typography>
      </Alert>
    </Box>
  );
}